import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Camera, House, ChartLine, User, Notebook, UsersThree, 
  Fire, Drop, Lightning, Lock, Check, Trophy, Warning,
  CaretRight, Image, Pencil, ArrowsClockwise, Heart, Star
} from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, API } from "../App";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import WelcomeVideo from "../components/WelcomeVideo";

// Loading messages for professional branding
const LOADING_MESSAGES = [
  "Analisando a sua refeição...",
  "Calculando valores nutricionais...",
  "A preparar o seu feedback de saúde...",
  "Identificando ingredientes..."
];

// Camera Modal States
const MODAL_STATE = {
  CLOSED: 'closed',
  CAPTURE: 'capture',
  ANALYZING: 'analyzing',
  RESULT: 'result',
  EDIT: 'edit',
  REANALYZING: 'reanalyzing',
  SAVING: 'saving'
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWelcomeVideo, setShowWelcomeVideo] = useState(false);
  
  // Camera modal state machine - single state variable
  const [modalState, setModalState] = useState(MODAL_STATE.CLOSED);
  const [capturedImage, setCapturedImage] = useState(null);
  const [analyzedFood, setAnalyzedFood] = useState(null);
  const [editableName, setEditableName] = useState("");
  const [editableState, setEditableState] = useState("");
  const [portionModifier, setPortionModifier] = useState(1);
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0]);
  const [foodDescription, setFoodDescription] = useState("");
  
  // Other modals
  const [showCongrats, setShowCongrats] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  
  // Weekly report and plateau
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [plateauData, setPlateauData] = useState(null);
  const [showPlateauModal, setShowPlateauModal] = useState(false);
  const [applyingAdjustment, setApplyingAdjustment] = useState(false);
  
  // Streak data
  const [streakData, setStreakData] = useState({ streak: 0, logged_today: false });
  
  // Favorites data
  const [favorites, setFavorites] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [savingFavorite, setSavingFavorite] = useState(false);
  
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const loadingIntervalRef = useRef(null);
  const isMountedRef = useRef(true);

  const isSubscriptionActive = useMemo(() => {
    if (!user) return false;
    
    // Check if status is 'ativo'
    if (user.subscription_status !== 'ativo') return false;
    
    // Check if subscription has expired (for trial users)
    if (user.expiry_date) {
      const expiryDate = new Date(user.expiry_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (expiryDate < today) return false;
    }
    
    return true;
  }, [user]);

  const isModalOpen = modalState !== MODAL_STATE.CLOSED;

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
      }
    };
  }, []);

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboard();
  }, [user?.goal]);

  // Check if should show welcome video (from API, not localStorage)
  useEffect(() => {
    if (dashboardData?.show_welcome_video && user?.goal) {
      // Small delay to let dashboard load first
      setTimeout(() => {
        setShowWelcomeVideo(true);
      }, 1000);
    }
  }, [dashboardData?.show_welcome_video, user?.goal]);

  const handleCloseWelcomeVideo = async () => {
    setShowWelcomeVideo(false);
    // Mark as seen in the database
    try {
      await axios.post(`${API}/users/mark-welcome-video-seen`);
    } catch (error) {
      console.log("Error marking video as seen:", error);
    }
  };

  // Cycle loading messages during analysis
  useEffect(() => {
    if (modalState === MODAL_STATE.ANALYZING || modalState === MODAL_STATE.REANALYZING) {
      let idx = 0;
      loadingIntervalRef.current = setInterval(() => {
        idx = (idx + 1) % LOADING_MESSAGES.length;
        if (isMountedRef.current) {
          setLoadingMessage(LOADING_MESSAGES[idx]);
        }
      }, 2000);
    } else {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
        loadingIntervalRef.current = null;
      }
    }
    return () => {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
        loadingIntervalRef.current = null;
      }
    };
  }, [modalState]);

  const fetchDashboard = async () => {
    try {
      // Fetch all data in parallel
      const [dashboardRes, weeklyRes, plateauRes, streakRes, favoritesRes] = await Promise.all([
        axios.get(`${API}/progress/dashboard`),
        axios.get(`${API}/progress/weekly-report`).catch(() => ({ data: null })),
        axios.get(`${API}/progress/plateau-check`).catch(() => ({ data: null })),
        axios.get(`${API}/streak`).catch(() => ({ data: { streak: 0 } })),
        axios.get(`${API}/meals/favorites`).catch(() => ({ data: { favorites: [] } }))
      ]);
      
      if (isMountedRef.current) {
        setDashboardData(dashboardRes.data);
        setWeeklyReport(weeklyRes.data);
        setPlateauData(plateauRes.data);
        setStreakData(streakRes.data || { streak: 0 });
        setFavorites(favoritesRes.data?.favorites || []);
        
        // Check for goal achievement
        const { calories } = dashboardRes.data;
        if (calories.goal_achieved && calories.consumed > 0) {
          const shown = sessionStorage.getItem('congrats_shown_today');
          if (!shown) {
            setShowCongrats(true);
            sessionStorage.setItem('congrats_shown_today', 'true');
          }
        }
        
        // Show plateau modal if detected (once per session)
        if (plateauRes.data?.is_plateau) {
          const plateauShown = sessionStorage.getItem('plateau_shown');
          if (!plateauShown) {
            setShowPlateauModal(true);
            sessionStorage.setItem('plateau_shown', 'true');
          }
        }
      }
    } catch (error) {
      console.error('Dashboard error:', error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  // Safe state reset - completely resets modal state
  const resetModalState = useCallback(() => {
    if (!isMountedRef.current) return;
    
    // Clear interval first
    if (loadingIntervalRef.current) {
      clearInterval(loadingIntervalRef.current);
      loadingIntervalRef.current = null;
    }
    
    // Reset all modal-related state in one batch
    setCapturedImage(null);
    setAnalyzedFood(null);
    setEditableName("");
    setEditableState("");
    setPortionModifier(1);
    setLoadingMessage(LOADING_MESSAGES[0]);
    setFoodDescription("");
    setModalState(MODAL_STATE.CLOSED);
  }, []);

  // Apply plateau adjustment
  const handlePlateauAdjustment = async (type, value) => {
    setApplyingAdjustment(true);
    try {
      const response = await axios.post(`${API}/progress/apply-plateau-adjustment`, {
        type,
        value
      });
      toast.success(response.data.message);
      setShowPlateauModal(false);
      // Refresh dashboard data
      fetchDashboard();
    } catch (error) {
      toast.error("Erro ao aplicar ajuste");
    } finally {
      setApplyingAdjustment(false);
    }
  };

  // Close modal with animation delay
  const closeModal = useCallback(() => {
    if (!isMountedRef.current) return;
    
    // First set to closed, then reset after animation
    setModalState(MODAL_STATE.CLOSED);
    
    // Use requestAnimationFrame for smooth transition
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (isMountedRef.current) {
          setCapturedImage(null);
          setAnalyzedFood(null);
          setEditableName("");
          setEditableState("");
          setPortionModifier(1);
          setLoadingMessage(LOADING_MESSAGES[0]);
        }
      }, 300); // Wait for modal close animation
    });
  }, []);

  const handleCameraClick = () => {
    if (!isSubscriptionActive) {
      setShowPaywall(true);
      return;
    }
    setModalState(MODAL_STATE.CAPTURE);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !isMountedRef.current) return;

    // Reset and start fresh
    setAnalyzedFood(null);
    setEditableName("");
    setEditableState("");
    setLoadingMessage(LOADING_MESSAGES[0]);
    setModalState(MODAL_STATE.ANALYZING);
    
    const reader = new FileReader();
    reader.onloadend = async () => {
      if (!isMountedRef.current) return;
      const imageData = reader.result;
      setCapturedImage(imageData);
      await analyzeFood(imageData);
    };
    reader.readAsDataURL(file);
    
    // Clear input for re-selection
    e.target.value = '';
  };

  const analyzeFood = async (imageBase64, correctionText = null) => {
    if (!isMountedRef.current) return;
    
    try {
      const payload = {
        portion_modifier: portionModifier,
        correction: correctionText
      };
      // Add image if provided
      if (imageBase64) {
        payload.image_base64 = imageBase64;
      }
      // Add text description
      if (foodDescription.trim()) {
        payload.food_description = foodDescription.trim();
      }
      
      const response = await axios.post(`${API}/meals/analyze`, payload);
      
      if (!isMountedRef.current) return;
      
      const data = response.data;
      const analysis = data.analysis || data;
      setAnalyzedFood(analysis);
      setEditableName(analysis.name || "");
      setEditableState("");
      setModalState(MODAL_STATE.RESULT);
      
      if (data.error || data.error_detail) {
        toast.error(`Erro: ${data.error_detail || data.error}`);
      } else if ((analysis.calories || 0) > 0) {
        const method = data.method === 'table' ? ' (calculado)' : '';
        toast.success(`${analysis.name} identificado${method}!`);
      } else {
        toast.warning("Não foi possível identificar a comida.");
      }
    } catch (error) {
      console.error("Analysis error:", error);
      if (!isMountedRef.current) return;
      
      if (error.response?.status === 403) {
        setShowPaywall(true);
        resetModalState();
      } else {
        const errorMsg = error.response?.data?.detail || error.message || "Erro ao analisar";
        toast.error(errorMsg);
        setAnalyzedFood({
          name: "Erro",
          description: errorMsg,
          calories: 0, protein: 0, carbs: 0, fat: 0,
          error: errorMsg
        });
        setModalState(MODAL_STATE.RESULT);
      }
    }
  };

  // Handle edit button click
  const handleEditClick = useCallback(() => {
    if (!isMountedRef.current) return;
    setModalState(MODAL_STATE.EDIT);
  }, []);

  // Handle cancel edit
  const handleCancelEdit = useCallback(() => {
    if (!isMountedRef.current) return;
    setEditableName(analyzedFood?.name || "");
    setEditableState("");
    setModalState(MODAL_STATE.RESULT);
  }, [analyzedFood]);

  // Reanalyze with correction
  const handleReanalyze = useCallback(async () => {
    if (!capturedImage || !isMountedRef.current) return;
    
    // Build correction text
    let correctionText = "";
    if (editableName && editableName !== analyzedFood?.name) {
      correctionText += `O nome correto é: ${editableName}. `;
    }
    if (editableState) {
      correctionText += `O estado de preparo é: ${editableState}. `;
    }
    
    if (!correctionText) {
      toast.info("Nenhuma correção detectada.");
      setModalState(MODAL_STATE.RESULT);
      return;
    }

    setLoadingMessage("Recalculando com as suas correções...");
    setModalState(MODAL_STATE.REANALYZING);
    
    try {
      const response = await axios.post(`${API}/meals/analyze`, {
        image_base64: capturedImage,
        portion_modifier: portionModifier,
        correction: correctionText.trim()
      });
      
      if (!isMountedRef.current) return;
      
      const data = response.data;
      setAnalyzedFood(data);
      setEditableName(data.name || "");
      setEditableState("");
      setModalState(MODAL_STATE.RESULT);
      
      if (data.calories > 0) {
        toast.success(`Recalculado: ${data.name}`);
      }
    } catch (error) {
      console.error("Reanalysis error:", error);
      if (isMountedRef.current) {
        toast.error("Erro ao recalcular");
        setModalState(MODAL_STATE.RESULT);
      }
    }
  }, [capturedImage, editableName, editableState, analyzedFood, portionModifier]);

  // Confirm and save meal - FIXED VERSION
  const confirmAndSaveMeal = useCallback(async () => {
    if (!analyzedFood || analyzedFood.error || !isMountedRef.current) return;
    
    setModalState(MODAL_STATE.SAVING);
    
    try {
      const response = await axios.post(`${API}/meals`, {
        name: editableName || analyzedFood.name,
        description: analyzedFood.description || "",
        calories: analyzedFood.calories,
        protein: analyzedFood.protein,
        carbs: analyzedFood.carbs,
        fat: analyzedFood.fat,
        fiber: analyzedFood.fiber || 0,
        is_angolan: analyzedFood.is_angolan || false,
        portion_modifier: portionModifier,
        image_base64: capturedImage,  // Send image to save (null if text calculation)
        health_status: analyzedFood.health_status || null,
        health_feedback: analyzedFood.health_feedback || null
      });
      
      if (!isMountedRef.current) return;
      
      const success = response.data.success;
      const newStreak = response.data.streak;
      
      // IMPORTANT: Close modal FIRST, then show messages and refresh
      closeModal();
      
      // Show toast after modal is closing
      if (success) {
        toast.success("Refeição salva com sucesso!");
        
        // Show streak celebration if streak increased
        if (newStreak && newStreak > streakData.streak) {
          setTimeout(() => {
            if (newStreak >= 7) {
              toast("🔥 Uma semana completa! Incrível!", { icon: "🏆" });
            } else if (newStreak >= 3) {
              toast(`🔥 ${newStreak} dias seguidos!`, { icon: "⭐" });
            } else if (newStreak === 1) {
              toast("🔥 Sequência iniciada!", { icon: "✨" });
            }
          }, 1000);
        }
      } else {
        toast.warning(response.data.warning || "Refeição registrada");
      }
      
      // Refresh dashboard in background
      fetchDashboard().then(() => {
        // Check goal achievement after refresh
        if (isMountedRef.current) {
          axios.get(`${API}/progress/dashboard`).then(res => {
            if (isMountedRef.current && res.data.calories?.goal_achieved) {
              setTimeout(() => {
                if (isMountedRef.current) {
                  setShowCongrats(true);
                }
              }, 500);
            }
          }).catch(() => {});
        }
      });
      
    } catch (error) {
      console.error("Save error:", error);
      if (isMountedRef.current) {
        toast.error("Erro ao salvar: " + (error.response?.data?.detail || error.message));
        setModalState(MODAL_STATE.RESULT);
      }
    }
  }, [analyzedFood, editableName, portionModifier, closeModal, fetchDashboard, capturedImage, streakData.streak]);

  // Save as favorite
  const saveAsFavorite = useCallback(async () => {
    if (!analyzedFood || !foodDescription.trim()) {
      toast.error("Favoritos funcionam apenas com refeições calculadas por texto");
      return;
    }
    
    setSavingFavorite(true);
    try {
      const response = await axios.post(`${API}/meals/favorites`, {
        name: editableName || analyzedFood.name,
        food_description: foodDescription.trim(),
        calories: analyzedFood.calories,
        protein: analyzedFood.protein,
        carbs: analyzedFood.carbs,
        fat: analyzedFood.fat,
        fiber: analyzedFood.fiber || 0
      });
      
      if (response.data.success) {
        toast.success("Guardado nos favoritos! ⭐");
        // Refresh favorites
        const favRes = await axios.get(`${API}/meals/favorites`).catch(() => ({ data: { favorites: [] } }));
        setFavorites(favRes.data?.favorites || []);
      } else {
        toast.error(response.data.error || "Erro ao guardar favorito");
      }
    } catch (error) {
      console.error("Favorite error:", error);
      toast.error("Funcionalidade em breve disponível");
    } finally {
      setSavingFavorite(false);
    }
  }, [analyzedFood, foodDescription, editableName]);

  // Handle using a favorite - fill description and trigger calculation
  const handleUseFavorite = useCallback(async (favorite) => {
    setFoodDescription(favorite.food_description);
    setShowFavorites(false);
    setCapturedImage(null);  // Clear any image
    setModalState(MODAL_STATE.ANALYZING);
    setLoadingMessage("A calcular refeição favorita...");
    
    // Small delay to ensure state is updated before calling analyze
    setTimeout(async () => {
      try {
        const payload = {
          portion_modifier: 1,
          food_description: favorite.food_description
        };
        
        const response = await axios.post(`${API}/meals/analyze`, payload);
        
        if (!isMountedRef.current) return;
        
        const data = response.data;
        const analysis = data.analysis || data;
        setAnalyzedFood(analysis);
        setEditableName(analysis.name || "");
        setModalState(MODAL_STATE.RESULT);
        toast.success(`${favorite.name} carregado!`);
      } catch (error) {
        console.error("Favorite analysis error:", error);
        toast.error("Erro ao calcular favorito");
        setModalState(MODAL_STATE.CAPTURE);
      }
    }, 100);
  }, []);

  // Adjust portion
  const adjustPortion = useCallback(async (modifier) => {
    if (!capturedImage || !isMountedRef.current) return;
    setPortionModifier(modifier);
    setLoadingMessage(LOADING_MESSAGES[0]);
    setModalState(MODAL_STATE.ANALYZING);
    await analyzeFood(capturedImage);
  }, [capturedImage]);

  // Health badge helper
  const getHealthBadge = useMemo(() => (feedback) => {
    if (!feedback) return null;
    const lower = feedback.toLowerCase();
    if (lower.includes('saudável') || lower.includes('equilibrad')) {
      return { label: "Saudável", color: "bg-primary text-white" };
    } else if (lower.includes('calóric') || lower.includes('pesad')) {
      return { label: "Calórica", color: "bg-zest text-white" };
    } else if (lower.includes('pobre') || lower.includes('atenção')) {
      return { label: "Atenção", color: "bg-yellow-500 text-white" };
    }
    return { label: "Equilibrada", color: "bg-blue-500 text-white" };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <div className="text-primary font-bold text-xl">Nzadi Fit</div>
          <div className="text-muted-foreground text-sm mt-1">Carregando...</div>
        </div>
      </div>
    );
  }

  const { calories, weight, meals_today, alerts } = dashboardData || {};
  const caloriePercent = calories?.percent || 0;

  const weightChartData = weight?.history?.slice().reverse().map(w => ({
    date: new Date(w.date).toLocaleDateString('pt', { day: '2-digit', month: 'short' }),
    weight: w.weight
  })) || [];

  // Determine what to show in modal based on state
  const isAnalyzing = modalState === MODAL_STATE.ANALYZING || modalState === MODAL_STATE.REANALYZING;
  const isSaving = modalState === MODAL_STATE.SAVING;
  const isEditing = modalState === MODAL_STATE.EDIT;
  const showResult = modalState === MODAL_STATE.RESULT && analyzedFood && !analyzedFood.error;

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Welcome Video Modal */}
      <AnimatePresence>
        {showWelcomeVideo && user?.goal && (
          <WelcomeVideo 
            goal={user.goal === 'perder' ? 'emagrecimento' : user.goal} 
            onClose={handleCloseWelcomeVideo} 
          />
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="bg-primary text-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/80 text-sm">Olá,</p>
            <h1 className="text-2xl font-bold">{dashboardData?.user?.name || user?.name}</h1>
          </div>
          <div className="flex items-center gap-4">
            {/* Streak Badge */}
            {streakData.streak > 0 && (
              <div 
                className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full"
                data-testid="streak-badge"
              >
                <span className="text-xl">🔥</span>
                <span className="font-bold text-lg">{streakData.streak}</span>
                <span className="text-white/80 text-xs">dias</span>
              </div>
            )}
            <div className="text-right">
              <p className="text-white/80 text-sm">Meta:</p>
              <p className="font-bold">{dashboardData?.user?.goal === 'perder' ? '🔥 Perder' : dashboardData?.user?.goal === 'ganhar' ? '💪 Ganhar' : '⚖️ Manter'}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="p-4 space-y-4">
        {/* Goal Achieved Banner */}
        {calories?.goal_achieved && calories?.consumed > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-r from-primary to-primary/80 text-white rounded-2xl p-4 flex items-center gap-3"
          >
            <div className="text-3xl">🎉</div>
            <div>
              <p className="font-bold">Parabéns! Meta batida!</p>
              <p className="text-sm text-white/90">Alcançaste a tua meta de calorias hoje!</p>
            </div>
          </motion.div>
        )}

        {/* Streak Card - Gamification */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-2xl p-4 ${
            streakData.streak >= 7 
              ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' 
              : streakData.streak >= 3 
                ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white'
                : 'bg-muted/50 border border-muted'
          }`}
          data-testid="streak-card"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`text-4xl ${streakData.streak > 0 ? 'animate-pulse' : 'opacity-50'}`}>
                🔥
              </div>
              <div>
                <p className={`font-bold text-lg ${streakData.streak >= 3 ? 'text-white' : 'text-foreground'}`}>
                  {streakData.streak > 0 
                    ? `${streakData.streak} ${streakData.streak === 1 ? 'dia' : 'dias'} seguidos!`
                    : 'Começa a tua sequência!'
                  }
                </p>
                <p className={`text-sm ${streakData.streak >= 3 ? 'text-white/80' : 'text-muted-foreground'}`}>
                  {streakData.logged_today 
                    ? '✅ Hoje já registaste' 
                    : streakData.streak > 0 
                      ? '⏰ Regista hoje para manter!'
                      : 'Regista uma refeição para começar'
                  }
                </p>
              </div>
            </div>
            {streakData.streak >= 7 && (
              <div className="text-3xl">🏆</div>
            )}
            {streakData.streak >= 3 && streakData.streak < 7 && (
              <div className="text-3xl">⭐</div>
            )}
          </div>
          {streakData.streak >= 5 && (
            <div className={`mt-3 pt-3 border-t ${streakData.streak >= 3 ? 'border-white/20' : 'border-muted'}`}>
              <p className={`text-xs ${streakData.streak >= 3 ? 'text-white/70' : 'text-muted-foreground'}`}>
                {streakData.streak >= 30 ? '🌟 Mestre da consistência!' :
                 streakData.streak >= 14 ? '💪 Duas semanas! Incrível!' :
                 streakData.streak >= 7 ? '🔥 Uma semana completa!' :
                 '🎯 Mais 2 dias para a próxima conquista!'}
              </p>
            </div>
          )}
        </motion.div>

        {/* Alerts */}
        {alerts?.plateau && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zest/10 border border-zest/30 rounded-2xl p-4 flex items-center gap-3"
          >
            <Warning size={24} className="text-zest" weight="duotone" />
            <div>
              <p className="font-bold text-foreground">Plateau detectado</p>
              <p className="text-sm text-muted-foreground">Considere ajustar as calorias em 10%</p>
            </div>
          </motion.div>
        )}

        {/* Calories Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-glass"
          data-testid="calories-card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Fire size={24} className="text-zest" weight="duotone" />
              <span className="font-bold text-foreground">Calorias Hoje</span>
            </div>
            <span className="text-sm text-muted-foreground">{Math.round(caloriePercent)}%</span>
          </div>
          
          <div className="text-center mb-4">
            <p className="text-4xl font-bold text-foreground font-mono">{calories?.consumed || 0}</p>
            <p className="text-muted-foreground">de {calories?.goal || 2000} kcal</p>
          </div>
          
          <Progress value={Math.min(caloriePercent, 100)} className="h-3 bg-muted" />
          
          <p className="text-center mt-3 text-sm text-muted-foreground">
            {calories?.remaining > 0 
              ? `Faltam ${calories.remaining} kcal` 
              : calories?.goal_achieved 
                ? "🎉 Meta alcançada!" 
                : "⚠️ Meta ultrapassada"}
          </p>
        </motion.div>

        {/* Macros Row */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Proteína", value: meals_today?.reduce((sum, m) => sum + (m.protein || 0), 0) || 0, unit: "g", icon: Lightning, color: "text-primary" },
            { label: "Carbos", value: meals_today?.reduce((sum, m) => sum + (m.carbs || 0), 0) || 0, unit: "g", icon: Drop, color: "text-blue-500" },
            { label: "Gordura", value: meals_today?.reduce((sum, m) => sum + (m.fat || 0), 0) || 0, unit: "g", icon: Fire, color: "text-zest" }
          ].map(({ label, value, unit, icon: Icon, color }) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl p-4 shadow-card text-center"
            >
              <Icon size={20} className={color} weight="duotone" />
              <p className="text-xl font-bold font-mono mt-1">{Math.round(value)}{unit}</p>
              <p className="text-xs text-muted-foreground">{label}</p>
            </motion.div>
          ))}
        </div>

        {/* Weight Progress */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card-glass"
          data-testid="weight-card"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="font-bold text-foreground">Progresso de Peso</span>
            <span className="text-sm text-primary font-medium">{weight?.progress_label}</span>
          </div>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="text-center">
              <p className="text-2xl font-bold font-mono">{weight?.current || '--'}</p>
              <p className="text-xs text-muted-foreground">Atual (kg)</p>
            </div>
            <div className="flex-1">
              <Progress value={weight?.progress_percent || 0} className="h-2" />
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold font-mono text-primary">{weight?.target || '--'}</p>
              <p className="text-xs text-muted-foreground">Meta (kg)</p>
            </div>
          </div>

          {weightChartData.length > 1 && (
            <div className="h-32 -mx-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weightChartData}>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis domain={['auto', 'auto']} hide />
                  <Tooltip />
                  <Line type="monotone" dataKey="weight" stroke="#2E7D32" strokeWidth={2} dot={{ fill: '#2E7D32', strokeWidth: 0 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </motion.div>

        {/* Weekly Progress Report */}
        {weeklyReport && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="card-glass"
            data-testid="weekly-report-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{weeklyReport.performance?.emoji || '📊'}</span>
                <span className="font-bold text-foreground">Resumo Semanal</span>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${
                weeklyReport.performance?.status === 'excellent' ? 'bg-green-100 text-green-700' :
                weeklyReport.performance?.status === 'good' ? 'bg-blue-100 text-blue-700' :
                weeklyReport.performance?.status === 'needs_attention' ? 'bg-amber-100 text-amber-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {weeklyReport.weight_progress?.days_logged || 0} dias registados
              </span>
            </div>
            
            {/* Performance message */}
            <div className={`p-3 rounded-xl mb-4 ${
              weeklyReport.performance?.status === 'excellent' ? 'bg-green-50 border border-green-200' :
              weeklyReport.performance?.status === 'good' ? 'bg-blue-50 border border-blue-200' :
              weeklyReport.performance?.status === 'needs_attention' ? 'bg-amber-50 border border-amber-200' :
              'bg-gray-50 border border-gray-200'
            }`}>
              <p className="text-sm font-medium text-foreground">{weeklyReport.performance?.message}</p>
            </div>
            
            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted/50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold font-mono text-foreground">
                  {weeklyReport.weight_progress?.actual_change > 0 ? '+' : ''}{weeklyReport.weight_progress?.actual_change?.toFixed(1) || '0'}
                </p>
                <p className="text-xs text-muted-foreground">kg esta semana</p>
              </div>
              <div className="bg-muted/50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold font-mono text-foreground">
                  {weeklyReport.calorie_progress?.days_on_target || 0}/{weeklyReport.calorie_progress?.days_tracked || 0}
                </p>
                <p className="text-xs text-muted-foreground">dias na meta</p>
              </div>
            </div>
            
            {/* Remaining to goal */}
            {weeklyReport.weight_progress?.remaining_to_goal > 0 && (
              <div className="mt-4 pt-3 border-t border-muted">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Faltam para a meta:</span>
                  <span className="font-bold text-primary">
                    {weeklyReport.weight_progress.remaining_to_goal.toFixed(1)} kg
                    {weeklyReport.weight_progress.weeks_remaining > 0 && (
                      <span className="font-normal text-muted-foreground"> (~{weeklyReport.weight_progress.weeks_remaining} semanas)</span>
                    )}
                  </span>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Today's Meals */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-glass"
          data-testid="meals-card"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="font-bold text-foreground">Refeições Hoje</span>
            <button 
              onClick={() => navigate('/diary')}
              className="text-primary text-sm font-medium flex items-center gap-1"
              data-testid="view-diary-button"
            >
              Ver tudo <CaretRight size={16} />
            </button>
          </div>
          
          {meals_today?.length > 0 ? (
            <div className="space-y-3">
              {meals_today.slice(0, 3).map((meal, index) => (
                <div key={meal.id || `meal-${index}`} className="flex items-center justify-between bg-muted/50 rounded-xl p-3">
                  <div className="flex items-center gap-3">
                    {meal.image_url ? (
                      <img 
                        src={meal.image_url} 
                        alt={meal.name}
                        className="w-12 h-12 rounded-lg object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-xl"
                      style={{ display: meal.image_url ? 'none' : 'flex' }}
                    >
                      🍽️
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{meal.name || 'Refeição'}</p>
                      <p className="text-xs text-muted-foreground">
                        {meal.created_at ? new Date(meal.created_at).toLocaleTimeString('pt', { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                      </p>
                    </div>
                  </div>
                  <p className="font-bold text-foreground font-mono">{meal.calories || 0} kcal</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Nenhuma refeição registrada hoje.<br />
              Toca no botão da câmera para começar!
            </p>
          )}
        </motion.div>

        {/* WhatsApp VIP */}
        {isSubscriptionActive && user?.subscription_status === 'ativo' && (
          <motion.a
            href="https://chat.whatsapp.com/F4fcyvt5D0jJNOFrY5V2j6"
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="block bg-green-500 text-white rounded-2xl p-4 text-center font-bold shadow-lg"
            data-testid="whatsapp-vip-button"
          >
            💬 Entrar no Grupo VIP WhatsApp
          </motion.a>
        )}
      </main>

      {/* Floating Camera Button */}
      <motion.button
        onClick={handleCameraClick}
        className="fixed bottom-24 right-4 w-16 h-16 bg-primary text-white rounded-full shadow-button flex items-center justify-center z-40"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        data-testid="camera-fab"
      >
        {isSubscriptionActive ? <Camera size={28} weight="bold" /> : <Lock size={28} weight="bold" />}
      </motion.button>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-muted p-2 flex justify-around items-center z-50 safe-bottom">
        {[
          { icon: House, label: "Início", path: "/", active: true },
          { icon: Notebook, label: "Diário", path: "/diary" },
          { icon: ChartLine, label: "Progresso", path: "/progress" },
          { icon: UsersThree, label: "Afiliados", path: "/affiliates" },
          { icon: User, label: "Perfil", path: "/profile" }
        ].map(({ icon: Icon, label, path, active }) => (
          <button
            key={path}
            onClick={() => navigate(path)}
            className={`flex flex-col items-center p-2 rounded-xl transition-colors ${
              active ? "text-primary" : "text-muted-foreground hover:text-primary"
            }`}
            data-testid={`nav-${label.toLowerCase()}`}
          >
            <Icon size={24} weight={active ? "fill" : "regular"} />
            <span className="text-xs mt-1">{label}</span>
          </button>
        ))}
      </nav>

      {/* Camera Modal - Custom implementation without Dialog */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeModal}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-3xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              {/* Header */}
              <div className="text-center mb-4">
                <h2 className="text-xl font-bold">Registrar Refeição</h2>
                {modalState === MODAL_STATE.CAPTURE && (
                  <p className="text-muted-foreground text-sm">Tira uma foto ou escolhe da galeria</p>
                )}
              </div>

              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" />
              <input ref={galleryInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />

              {/* Capture State */}
              {modalState === MODAL_STATE.CAPTURE && (
                <div className="space-y-4">
                  <button
                    onClick={() => cameraInputRef.current?.click()}
                    className="w-full h-32 bg-primary/10 border-2 border-primary/30 rounded-2xl flex flex-col items-center justify-center gap-3 hover:bg-primary/20 transition-colors"
                    data-testid="capture-camera-button"
                  >
                    <Camera size={40} className="text-primary" weight="duotone" />
                    <span className="text-primary font-bold">Tirar Foto</span>
                  </button>

                  <button
                    onClick={() => galleryInputRef.current?.click()}
                    className="w-full h-24 bg-muted/50 border-2 border-muted rounded-2xl flex flex-col items-center justify-center gap-2 hover:bg-muted transition-colors"
                    data-testid="select-gallery-button"
                  >
                    <Image size={32} className="text-muted-foreground" weight="duotone" />
                    <span className="text-muted-foreground font-medium">Escolher da Galeria</span>
                  </button>

                  {/* Optional context field */}
                  <div className="pt-2 border-t border-muted">
                    <p className="text-xs text-muted-foreground mb-2 font-medium">Ou escreva os alimentos com gramas (sem foto):</p>
                    <input
                      type="text"
                      value={foodDescription}
                      onChange={(e) => setFoodDescription(e.target.value)}
                      placeholder="Ex: 150g de arroz e 100g de frango"
                      className="w-full px-3 py-2.5 text-sm border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                      data-testid="food-description-input"
                    />
                    {foodDescription.trim() && /\d+\s*g/i.test(foodDescription) && (
                      <button
                        onClick={() => {
                          setModalState(MODAL_STATE.ANALYZING);
                          setLoadingMessage("A calcular...");
                          analyzeFood(null);
                        }}
                        className="w-full mt-2 py-2.5 rounded-xl font-semibold text-sm bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                        data-testid="calculate-from-text-button"
                      >
                        Calcular sem foto
                      </button>
                    )}
                  </div>

                  {/* Favorites Section */}
                  {favorites.length > 0 && (
                    <div className="pt-3 border-t border-muted">
                      <button
                        onClick={() => setShowFavorites(!showFavorites)}
                        className="flex items-center justify-between w-full text-left"
                      >
                        <div className="flex items-center gap-2">
                          <Star size={18} className="text-amber-500" weight="fill" />
                          <span className="text-sm font-semibold text-foreground">Meus Favoritos</span>
                          <span className="text-xs bg-muted rounded-full px-2 py-0.5">{favorites.length}</span>
                        </div>
                        <CaretRight size={18} className={`text-muted-foreground transition-transform ${showFavorites ? 'rotate-90' : ''}`} />
                      </button>
                      
                      <AnimatePresence>
                        {showFavorites && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                              {favorites.map((fav) => (
                                <button
                                  key={fav.id}
                                  onClick={() => handleUseFavorite(fav)}
                                  className="w-full p-3 bg-white border border-muted rounded-xl text-left hover:bg-muted/50 transition-colors"
                                  data-testid={`favorite-${fav.id}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                      <p className="font-semibold text-sm truncate">{fav.name}</p>
                                      <p className="text-xs text-muted-foreground truncate">{fav.food_description}</p>
                                    </div>
                                    <span className="text-primary font-bold text-sm ml-2">{fav.calories} kcal</span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
              )}

              {/* Image Preview - Only when there's an image */}
              {capturedImage && modalState !== MODAL_STATE.CAPTURE && (
                <div className="space-y-4">
                  <img src={capturedImage} alt="Captured" className="w-full h-48 object-cover rounded-2xl" />
                  
                  {/* Loading State for AI analysis */}
                  {isAnalyzing && (
                    <motion.div 
                      initial={{ opacity: 0 }} 
                      animate={{ opacity: 1 }} 
                      className="text-center py-8 bg-primary/5 rounded-2xl"
                    >
                      <div className="relative w-16 h-16 mx-auto mb-4">
                        <div className="absolute inset-0 border-4 border-primary/20 rounded-full" />
                        <div className="absolute inset-0 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                      </div>
                      <p className="text-primary font-bold text-lg">{loadingMessage}</p>
                      <p className="text-muted-foreground text-sm mt-1">IA Nzadi Fit em ação</p>
                    </motion.div>
                  )}

                  {/* Saving State for AI analysis */}
                  {isSaving && (
                    <motion.div 
                      initial={{ opacity: 0 }} 
                      animate={{ opacity: 1 }} 
                      className="text-center py-8 bg-primary/5 rounded-2xl"
                    >
                      <div className="relative w-16 h-16 mx-auto mb-4">
                        <div className="absolute inset-0 border-4 border-primary/20 rounded-full" />
                        <div className="absolute inset-0 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                      </div>
                      <p className="text-primary font-bold text-lg">A guardar refeição...</p>
                    </motion.div>
                  )}

                  {/* Edit Mode - Only for AI analysis with image */}
                  {isEditing && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-muted/50 rounded-2xl p-4 space-y-4"
                    >
                      <h3 className="font-bold text-foreground flex items-center gap-2">
                        <Pencil size={20} className="text-primary" />
                        Corrigir Identificação
                      </h3>
                      
                      <div>
                        <label className="text-sm text-muted-foreground mb-1 block">Nome do alimento</label>
                        <input
                          type="text"
                          value={editableName}
                          onChange={(e) => setEditableName(e.target.value)}
                          placeholder="Ex: Peixe, Frango, Arroz..."
                          className="w-full bg-white border-2 border-muted rounded-xl px-4 py-3 outline-none focus:border-primary transition-colors"
                          data-testid="edit-name-input"
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm text-muted-foreground mb-1 block">Estado de preparo (opcional)</label>
                        <div className="flex flex-wrap gap-2">
                          {['Grelhado', 'Frito', 'Cozido', 'Assado', 'Cru'].map((state) => (
                            <button
                              key={state}
                              onClick={() => setEditableState(editableState === state ? '' : state)}
                              className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                                editableState === state 
                                  ? 'bg-primary text-white' 
                                  : 'bg-white text-foreground hover:bg-primary/10'
                              }`}
                            >
                              {state}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          onClick={handleCancelEdit}
                          className="flex-1 rounded-full"
                        >
                          Cancelar
                        </Button>
                        <Button
                          onClick={handleReanalyze}
                          className="flex-1 btn-primary"
                          data-testid="reanalyze-button"
                        >
                          <ArrowsClockwise size={20} className="mr-2" />
                          Recalcular
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </div>
              )}

              {/* Text Calculation Mode - No image, loading/saving states */}
              {!capturedImage && modalState !== MODAL_STATE.CAPTURE && (
                <div className="space-y-4">
                  {/* Loading State for text calculation */}
                  {isAnalyzing && (
                    <motion.div 
                      initial={{ opacity: 0 }} 
                      animate={{ opacity: 1 }} 
                      className="text-center py-8 bg-primary/5 rounded-2xl"
                    >
                      <div className="relative w-16 h-16 mx-auto mb-4">
                        <div className="absolute inset-0 border-4 border-primary/20 rounded-full" />
                        <div className="absolute inset-0 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                      </div>
                      <p className="text-primary font-bold text-lg">{loadingMessage}</p>
                      <p className="text-muted-foreground text-sm mt-1">A calcular pela tabela nutricional</p>
                    </motion.div>
                  )}

                  {/* Saving State for text calculation */}
                  {isSaving && (
                    <motion.div 
                      initial={{ opacity: 0 }} 
                      animate={{ opacity: 1 }} 
                      className="text-center py-8 bg-primary/5 rounded-2xl"
                    >
                      <div className="relative w-16 h-16 mx-auto mb-4">
                        <div className="absolute inset-0 border-4 border-primary/20 rounded-full" />
                        <div className="absolute inset-0 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                      </div>
                      <p className="text-primary font-bold text-lg">A guardar refeição...</p>
                    </motion.div>
                  )}
                </div>
              )}

              {/* Result View - Works for both AI analysis and text calculation */}
              {modalState === MODAL_STATE.RESULT && analyzedFood && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`rounded-2xl p-4 ${analyzedFood.error ? 'bg-destructive/10' : 'bg-muted/50'}`}
                    >
                      {analyzedFood.error && (
                        <div className="mb-4 p-3 bg-destructive/20 rounded-xl">
                          <p className="text-destructive font-bold text-sm">Erro na análise:</p>
                          <p className="text-destructive/80 text-xs mt-1 break-all">{analyzedFood.error}</p>
                        </div>
                      )}
                      
                      {!analyzedFood.error && (
                        <>
                          {/* Name with Edit Button and Badge */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <h3 className="font-bold text-lg">{editableName || analyzedFood.name}</h3>
                              {capturedImage && (
                                <button
                                  onClick={handleEditClick}
                                  className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
                                  data-testid="edit-food-button"
                                >
                                  <Pencil size={18} className="text-primary" />
                                </button>
                              )}
                            </div>
                            {analyzedFood.is_angolan && (
                              <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">
                                🇦🇴 Angolano
                              </span>
                            )}
                          </div>
                          
                          {/* Items Breakdown - Enhanced for table calculation */}
                          {analyzedFood.items && analyzedFood.items.length > 0 && (
                            <div className="mb-4 space-y-2 border-b border-muted pb-3">
                              {analyzedFood.items.map((item, idx) => (
                                <div key={idx} className="flex items-center justify-between bg-white rounded-lg px-3 py-2.5 shadow-sm">
                                  <div className="flex items-center gap-2 flex-1">
                                    <span className="text-sm font-medium text-foreground">{item.item}</span>
                                    <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">{item.quantity}</span>
                                  </div>
                                  <span className="text-sm font-bold text-primary">{item.calories} kcal</span>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Summary Stats Grid - Enhanced with fiber and totals */}
                          <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                            {/* Total Calories - Prominent */}
                            <div className="bg-gradient-to-br from-primary to-primary/80 rounded-xl p-3 text-center text-white col-span-2 sm:col-span-1">
                              <p className="text-3xl font-bold font-mono">{analyzedFood.calories}</p>
                              <p className="text-xs opacity-90">kcal</p>
                            </div>
                            {/* Protein */}
                            <div className="bg-white rounded-xl p-3 text-center shadow-sm">
                              <p className="text-xl font-bold font-mono text-emerald-600">{analyzedFood.protein}g</p>
                              <p className="text-xs text-muted-foreground">Proteína</p>
                            </div>
                            {/* Carbs */}
                            <div className="bg-white rounded-xl p-3 text-center shadow-sm">
                              <p className="text-xl font-bold font-mono text-amber-600">{analyzedFood.carbs}g</p>
                              <p className="text-xs text-muted-foreground">Carbos</p>
                            </div>
                            {/* Fat */}
                            <div className="bg-white rounded-xl p-3 text-center shadow-sm">
                              <p className="text-xl font-bold font-mono text-rose-500">{analyzedFood.fat}g</p>
                              <p className="text-xs text-muted-foreground">Gordura</p>
                            </div>
                            {/* Fiber - Only show for table calculation */}
                            {analyzedFood.calculated_from_table && analyzedFood.fiber !== undefined && (
                              <div className="bg-white rounded-xl p-3 text-center shadow-sm">
                                <p className="text-xl font-bold font-mono text-green-600">{analyzedFood.fiber}g</p>
                                <p className="text-xs text-muted-foreground">Fibras</p>
                              </div>
                            )}
                            {/* Total grams - Only for table calculation */}
                            {analyzedFood.calculated_from_table && analyzedFood.total_grams && (
                              <div className="bg-white rounded-xl p-3 text-center shadow-sm">
                                <p className="text-xl font-bold font-mono text-slate-600">{analyzedFood.total_grams}g</p>
                                <p className="text-xs text-muted-foreground">Total</p>
                              </div>
                            )}
                          </div>

                          {/* Health Status Badge & Feedback */}
                          {(analyzedFood.health_status || analyzedFood.health_feedback) && (
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className={`p-3 rounded-xl border-l-4 ${
                                analyzedFood.health_status === 'healthy' 
                                  ? 'bg-emerald-50 border-emerald-500' 
                                  : analyzedFood.health_status === 'moderate'
                                  ? 'bg-amber-50 border-amber-500'
                                  : 'bg-rose-50 border-rose-500'
                              }`}
                            >
                              <div className="flex items-start gap-2">
                                <span className={`inline-flex items-center text-xs px-2.5 py-1 rounded-full font-semibold ${
                                  analyzedFood.health_status === 'healthy'
                                    ? 'bg-emerald-500 text-white'
                                    : analyzedFood.health_status === 'moderate'
                                    ? 'bg-amber-500 text-white'
                                    : 'bg-rose-500 text-white'
                                }`}>
                                  {analyzedFood.health_status === 'healthy' && '✓ Saudável'}
                                  {analyzedFood.health_status === 'moderate' && '⚖ Moderada'}
                                  {analyzedFood.health_status === 'unhealthy' && '⚠ Atenção'}
                                  {!analyzedFood.health_status && getHealthBadge(analyzedFood.health_feedback)?.label}
                                </span>
                              </div>
                              {analyzedFood.health_feedback && (
                                <p className="text-sm text-foreground mt-2 leading-relaxed">{analyzedFood.health_feedback}</p>
                              )}
                            </motion.div>
                          )}

                          {/* Portion Modifier - Only for AI analysis with image */}
                          {capturedImage && (
                            <div className="mt-4">
                              <p className="text-sm text-muted-foreground mb-2">Ajustar quantidade:</p>
                              <div className="flex items-center justify-center gap-3">
                                {[0.5, 0.75, 1, 1.25, 1.5].map((mod) => (
                                  <button
                                    key={mod}
                                    onClick={() => adjustPortion(mod)}
                                    className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                                      portionModifier === mod ? "bg-primary text-white" : "bg-white text-foreground hover:bg-primary/10"
                                    }`}
                                  >
                                    {mod === 0.5 ? "½" : mod === 0.75 ? "¾" : mod === 1 ? "1x" : mod === 1.25 ? "1¼" : "1½"}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </motion.div>
                  )}
            </div>

            {/* Footer Buttons */}
            <div className="bg-muted/30 p-4 space-y-2">
              {/* Save as Favorite Button - Only for text calculations */}
              {showResult && analyzedFood?.calculated_from_table && foodDescription.trim() && (
                <button
                  onClick={saveAsFavorite}
                  disabled={savingFavorite}
                  className="w-full py-2.5 rounded-full text-sm font-medium border-2 border-amber-400 text-amber-600 hover:bg-amber-50 transition-colors flex items-center justify-center gap-2"
                  data-testid="save-favorite-button"
                >
                  {savingFavorite ? (
                    "A guardar..."
                  ) : (
                    <>
                      <Star size={18} weight="fill" className="text-amber-500" />
                      Guardar como Favorito
                    </>
                  )}
                </button>
              )}
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={closeModal} className="flex-1 rounded-full" disabled={isSaving}>
                  Cancelar
                </Button>
                {showResult && (
                  <Button onClick={confirmAndSaveMeal} disabled={isSaving} className="flex-1 btn-primary">
                    {isSaving ? "A salvar..." : <><Check size={20} className="mr-2" /> Adicionar Refeição</>}
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Congratulations Modal */}
      {showCongrats && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowCongrats(false)}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl max-w-sm w-full p-6 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", bounce: 0.5 }}
              className="w-24 h-24 bg-gradient-to-br from-primary to-primary/80 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <Trophy size={48} className="text-white" weight="fill" />
            </motion.div>
            <h2 className="text-2xl font-bold mb-2">Parabéns! 🎉</h2>
            <p className="text-muted-foreground">
              Alcançaste a tua meta de calorias hoje! Continua assim!
            </p>
            <Button onClick={() => setShowCongrats(false)} className="btn-primary mt-6">
              Obrigado(a)!
            </Button>
          </motion.div>
        </div>
      )}

      {/* Plateau Detection Modal */}
      {showPlateauModal && plateauData?.is_plateau && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowPlateauModal(false)}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Warning size={32} className="text-amber-600" weight="duotone" />
              </div>
              <h2 className="text-xl font-bold text-foreground">Platô Detectado</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {plateauData.message}
              </p>
            </div>
            
            <div className="bg-amber-50 rounded-xl p-4 mb-4">
              <p className="text-sm text-amber-800">
                <strong>Análise:</strong> O teu peso variou apenas {Math.abs(plateauData.weight_change_21d || 0).toFixed(1)}kg nos últimos {plateauData.days_analyzed || 21} dias.
              </p>
              {plateauData.suggestion && (
                <p className="text-sm text-amber-800 mt-2">
                  <strong>Sugestão:</strong> {plateauData.suggestion}
                </p>
              )}
            </div>
            
            <div className="space-y-3">
              {plateauData.suggested_calories && (
                <Button 
                  onClick={() => handlePlateauAdjustment('calories', plateauData.suggested_calories)}
                  disabled={applyingAdjustment}
                  className="btn-primary w-full"
                  data-testid="adjust-calories-btn"
                >
                  {applyingAdjustment ? 'A aplicar...' : `Ajustar para ${plateauData.suggested_calories} kcal/dia`}
                </Button>
              )}
              
              {plateauData.suggestion_type === 'increase_intensity' && plateauData.current_intensity !== 'agressivo' && (
                <Button 
                  onClick={() => handlePlateauAdjustment('intensity', 'agressivo')}
                  disabled={applyingAdjustment}
                  variant="outline"
                  className="w-full"
                  data-testid="increase-intensity-btn"
                >
                  Aumentar intensidade para Agressivo
                </Button>
              )}
              
              <button 
                onClick={() => setShowPlateauModal(false)} 
                className="w-full text-muted-foreground text-sm py-2 hover:text-foreground transition-colors"
              >
                Manter configuração atual
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Paywall Modal */}
      {showPaywall && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowPaywall(false)}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl max-w-sm w-full p-6 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock size={40} className="text-primary" weight="duotone" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Membro Nzadi Fit</h2>
            <p className="text-muted-foreground mb-6">
              Esta funcionalidade é exclusiva para membros Nzadi Fit. Ative o seu plano para começar!
            </p>
            <Button onClick={() => { setShowPaywall(false); navigate('/payment'); }} className="btn-primary w-full" data-testid="upgrade-button">
              Ativar Plano
            </Button>
            <button 
              onClick={() => setShowPaywall(false)} 
              className="text-muted-foreground text-sm mt-4 hover:text-foreground transition-colors"
            >
              Agora não
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
